import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { unbilledTimeAPI } from '../services/api';
import { UnbilledSummaryResponse, UnbilledCustomerSummary } from '../types';

interface UnbilledSummaryProps {
  /** Maximum number of customers to show in the summary */
  maxItems?: number;
  /** Whether to show the full view link */
  showViewAllLink?: boolean;
}

const UnbilledSummary: React.FC<UnbilledSummaryProps> = ({ 
  maxItems = 5,
  showViewAllLink = true 
}) => {
  const [data, setData] = useState<UnbilledSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await unbilledTimeAPI.getSummary();
      setData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load unbilled time summary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatHours = (hours: number) => {
    return hours.toFixed(1);
  };

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>Unbilled Time</h3>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>Unbilled Time</h3>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#e74c3c' }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data || data.customers.length === 0) {
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>Unbilled Time</h3>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#27ae60' }}>
          All billable time has been invoiced!
        </div>
      </div>
    );
  }

  const displayCustomers = data.customers.slice(0, maxItems);
  const hasMore = data.customers.length > maxItems;

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>Unbilled Time</h3>
        {showViewAllLink && (
          <Link to="/unbilled-time" className="btn btn-secondary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
            View Full Report
          </Link>
        )}
      </div>

      {/* Summary stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '1rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50' }}>
            {formatHours(data.grand_total_hours)}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Hours</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#27ae60' }}>
            {formatCurrency(data.grand_total_amount)}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Total Amount</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e67e22' }}>
            {data.total_entries_over_30_days}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Over 30 Days</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e74c3c' }}>
            {data.total_entries_over_60_days}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Over 60 Days</div>
        </div>
      </div>

      {/* Customer list */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Customer</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Hours</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Amount</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>Aging</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayCustomers.map((customer: UnbilledCustomerSummary) => (
              <tr key={customer.customer_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <div style={{ fontWeight: 500 }}>{customer.customer_name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>
                    {customer.entries_count} entries
                  </div>
                </td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                  {formatHours(customer.total_hours)}
                </td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 500, color: '#27ae60' }}>
                  {formatCurrency(customer.total_amount)}
                </td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                  {customer.entries_over_60_days > 0 ? (
                    <span style={{ 
                      display: 'inline-block',
                      padding: '0.2rem 0.5rem',
                      backgroundColor: '#fce4e4',
                      color: '#c0392b',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}>
                      {customer.entries_over_60_days} &gt;60d
                    </span>
                  ) : customer.entries_over_30_days > 0 ? (
                    <span style={{ 
                      display: 'inline-block',
                      padding: '0.2rem 0.5rem',
                      backgroundColor: '#fef3e2',
                      color: '#d35400',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}>
                      {customer.entries_over_30_days} &gt;30d
                    </span>
                  ) : (
                    <span style={{ color: '#27ae60', fontSize: '0.85rem' }}>✓</span>
                  )}
                </td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                  <Link 
                    to={`/invoices/new?customer=${customer.customer_id}`}
                    className="btn btn-primary"
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  >
                    Invoice
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <Link to="/unbilled-time" style={{ color: '#3498db' }}>
            View all {data.customers.length} customers with unbilled time →
          </Link>
        </div>
      )}
    </div>
  );
};

export default UnbilledSummary;


